import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BUYER_FORM_URL, VENDOR_FORM_URL } from "@/constants/links";

const links = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/about", label: "About", testId: "nav-link-about" },
  { to: "/marketplace", label: "Marketplace", testId: "nav-link-marketplace" },
  { to: "/how-it-works", label: "How It Works", testId: "nav-link-how-it-works" },
  { to: "/services", label: "Services", testId: "nav-link-services" },
  { to: "/contact", label: "Contact", testId: "nav-link-contact" },
];

const Wordmark = () => (
  <Link to="/" data-testid="nav-brand-logo" className="group flex items-center gap-3">
    <img
      src="/assets/aflimart-logo.png?v=2"
      alt="Aflimart Digital Market logo"
      className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
    />
    <span className="leading-none">
      <span className="block font-sans text-sm font-bold tracking-[0.22em] text-[#1E3A8A]">AFLIMART</span>
      <span className="mt-1 block font-sans text-[9px] font-semibold tracking-[0.32em] text-[#F97316]">DIGITAL MARKET</span>
    </span>
  </Link>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 bg-white/95 backdrop-blur-md transition-[border-color,box-shadow] duration-500 ${
        scrolled || open ? "border-b border-[#E2E8F0] shadow-sm" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
        <Wordmark />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `group relative font-sans text-[13px] font-semibold tracking-wide transition-colors duration-300 ${
                  isActive ? "text-[#1E3A8A]" : "text-[#0F172A]/70 hover:text-[#1E3A8A]"
                }`
              }
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-[#F97316] transition-all duration-300 group-hover:w-full" />
            </NavLink>
          ))}
          <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-waitlist-cta" className="btn-orange !px-6 !py-2.5 !text-xs">
            Join Waitlist <ArrowUpRight size={14} />
          </a>
        </nav>
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="text-[#1E3A8A] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-[#E2E8F0] bg-white/98 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testId}`}
                    className={({ isActive }) =>
                      `block py-3 font-sans text-xl font-semibold ${isActive ? "text-[#1E3A8A]" : "text-[#0F172A]/80"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-waitlist-cta" className="btn-orange mt-4 w-full">
                Join the Waitlist <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
